import { E as EmblaCarousel, d as debouncedWatchResize, e as extractSlideColorSchemes, b as duplicateSlidesForLoop, C as ClassNames, c as addLayoutShiftPrevention, a as addPrevNextBtnsClickHandlersSSR, f as addDotsBtnsAndClickHandlersSSR, g as addColorSchemeSyncHandlers } from "./embla-extension-cVbn2auS.js";
import CustomFade from "./embla-custom-fade-cX9wHv3_.js";
import { A as Autoplay } from "./embla-carousel-autoplay.esm-lYa57aZD.js";
import { requestIdleCallback, prefersReducedMotion } from "./utilities-BFAI3Dmv.js";
import { onScrollUpdate } from "./scroll-dispatcher-BIs-vu0Q.js";
import "./native-scroll-snap-Cd15iSfl.js";
import "./mobile-autoplay-LQAitT2O.js";
import "./progressive-images-mIwHxxsh.js";
import "./autoplay-progress-BLL6tdZq.js";
import "./waapi-patch-BCt4pEht.js";
import "./cookie-banner-layout-DZP7Q0uT.js";
import "./section-anchor-scroll-Bvm-mXVl.js";
import "./mobile-anim-gate-Btn1wVuD.js";
function stackedOnProgress(node) {
  node.style.opacity = "1";
}
function stackedOnCleanup(node) {
  node.style.opacity = "";
}
class ArticleSlideshow extends HTMLElement {
  constructor() {
    super();
    this.emblaApi = null;
    this.originalSlideCount = 0;
    this.slideColorSchemes = [];
    this.cleanupFunctions = [];
    this.nodes = {
      embla: null,
      container: null,
      viewport: null,
      prevBtn: null,
      nextBtn: null,
      slideshowDots: null,
      paginationContainer: null
    };
    this.config = {
      standardEffect: "fade",
      showNavigation: false,
      showSlideshowDots: false,
      cursorNavigation: false,
      autoPlay: false,
      options: {}
    };
  }
  connectedCallback() {
    this.cacheNodes();
    this.parseConfig();
    if (Shopify.designMode) {
      this.init();
    } else {
      requestIdleCallback(() => {
        if (this.isConnected && !this.isInteractionReady()) this.init();
      });
    }
  }
  isInteractionReady() {
    return !!this.emblaApi;
  }
  prepareInteraction() {
    if (!this.isInteractionReady()) this.init();
    return this.isInteractionReady();
  }
  disconnectedCallback() {
    this.destroy();
  }
  // ========== DOM & Configuration ==========
  cacheNodes() {
    this.nodes.embla = this.querySelector(".embla");
    if (!this.nodes.embla) {
      throw new Error("SlideshowV2: .embla element not found");
    }
    this.nodes.container = this.nodes.embla.querySelector(".embla__container");
    if (!this.nodes.container) {
      throw new Error("SlideshowV2: .embla__container element not found");
    }
    this.nodes.viewport = this.nodes.embla.querySelector(".embla__viewport");
    if (!this.nodes.viewport) {
      throw new Error("SlideshowV2: .embla__viewport element not found");
    }
    this.nodes.prevBtn = this.nodes.embla.querySelector(".embla__snip-button--prev button");
    this.nodes.nextBtn = this.nodes.embla.querySelector(".embla__snip-button--next button");
    this.nodes.navContainer = this.nodes.embla.querySelector(".embla__snip-navs");
    this.nodes.slideshowDots = this.nodes.embla.querySelector(".embla__snip-dots");
    this.nodes.paginationContainer = this.nodes.embla.querySelector(".featured-article-pagination");
  }
  parseConfig() {
    this.config.showNavigation = this.dataset.showNavigation === "true";
    this.config.showSlideshowDots = this.dataset.showSlideshowDots === "true";
    this.config.cursorNavigation = this.dataset.cursorNavigation === "true";
    this.config.standardEffect = this.dataset.animStandardEffect || "fade";
    this.config.options = this.parseJSON(this.dataset.options, {});
    this.config.autoPlay = this.parseJSON(this.dataset.autoPlay, false) || false;
  }
  parseJSON(jsonString, fallback) {
    if (!jsonString || !jsonString.trim()) {
      return fallback;
    }
    try {
      const parsed = JSON.parse(jsonString.replace(/,\s*([}\]])/g, "$1"));
      if (typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed;
      }
      return fallback;
    } catch (error) {
      return fallback;
    }
  }
  // ========== Initialization ==========
  init() {
    if (this.emblaApi) {
      this.destroy();
    }
    const slideCount = this.nodes.container.children.length;
    if (slideCount <= 1) {
      this.handleSingleSlide(slideCount);
      return;
    }
    try {
      const plugins = this.initPlugins();
      this.emblaApi = EmblaCarousel(this.nodes.viewport, { ...this.config.options, watchResize: debouncedWatchResize() }, plugins);
      let isTransitioning = false;
      let transitionTimeout;
      this.emblaApi.on("select", () => {
        isTransitioning = true;
        clearTimeout(transitionTimeout);
        transitionTimeout = setTimeout(() => {
          isTransitioning = false;
        }, 450);
      });
      this.emblaApi.on("settle", () => {
        isTransitioning = false;
        clearTimeout(transitionTimeout);
      });
      const origScrollNext = this.emblaApi.scrollNext;
      const origScrollPrev = this.emblaApi.scrollPrev;
      const origScrollTo = this.emblaApi.scrollTo;
      this.emblaApi.scrollNext = (...args) => {
        if (!isTransitioning) origScrollNext.call(this.emblaApi, ...args);
      };
      this.emblaApi.scrollPrev = (...args) => {
        if (!isTransitioning) origScrollPrev.call(this.emblaApi, ...args);
      };
      this.emblaApi.scrollTo = (...args) => {
        if (!isTransitioning) origScrollTo.call(this.emblaApi, ...args);
      };
      this.slideColorSchemes = extractSlideColorSchemes(this.nodes.container);
      this.originalSlideCount = this.nodes.container.children.length;
      if (this.config.options.loop) {
        this.originalSlideCount = duplicateSlidesForLoop(this.emblaApi, this.nodes.container);
      }
      this.setupFeatures();
      this.nodes.embla.classList.add("is-initialized");
      this.nodes.embla.classList.remove("is-first-load");
    } catch (error) {
      throw error;
    }
  }
  handleSingleSlide(slideCount) {
    var _a;
    this.slideColorSchemes = extractSlideColorSchemes(this.nodes.container);
    this.originalSlideCount = slideCount;
    (_a = this.nodes.container.firstElementChild) == null ? void 0 : _a.classList.add("is-snapped");
    if (this.nodes.paginationContainer && this.slideColorSchemes[0]) {
      this.nodes.paginationContainer.classList.add(this.slideColorSchemes[0]);
    }
    if (this.nodes.navContainer && this.slideColorSchemes[0]) {
      this.nodes.navContainer.classList.add(this.slideColorSchemes[0]);
    }
    this.nodes.embla.classList.add("is-initialized");
    this.nodes.embla.classList.remove("is-first-load");
  }
  initPlugins() {
    const plugins = [];
    plugins.push(ClassNames({
      snapped: "is-snapped",
      inView: ""
    }));
    if (this.config.autoPlay && !prefersReducedMotion()) {
      plugins.push(Autoplay(this.config.autoPlay));
    }
    const fadeOptions = this.config.standardEffect === "wipe" ? { onProgress: stackedOnProgress, onCleanup: stackedOnCleanup, alwaysStacked: true } : {};
    plugins.push(CustomFade(fadeOptions));
    return plugins;
  }
  setupFeatures() {
    const layoutShiftCleanup = addLayoutShiftPrevention(this.emblaApi, this.nodes.embla);
    this.registerCleanup(layoutShiftCleanup);
    if (this.config.showNavigation) {
      this.enableNavigation();
    }
    if (this.config.showSlideshowDots) {
      this.enableSlideshowDots();
    }
    if (this.config.cursorNavigation) {
      this.enableCursorNavigation();
    }
    this.enableColorSchemeSync();
  }
  // ========== Features ==========
  enableNavigation() {
    const { prevBtn, nextBtn } = this.nodes;
    if (!prevBtn || !nextBtn || !this.emblaApi) return;
    const cleanup = addPrevNextBtnsClickHandlersSSR(this.emblaApi, prevBtn, nextBtn);
    this.registerCleanup(cleanup);
  }
  enableSlideshowDots() {
    const node = this.nodes.slideshowDots;
    if (!node || !this.emblaApi) return;
    const originalCount = this.originalSlideCount > 0 ? this.originalSlideCount : null;
    const cleanup = addDotsBtnsAndClickHandlersSSR(this.emblaApi, node, originalCount);
    this.registerCleanup(cleanup);
  }
  enableCursorNavigation() {
    if (!this.emblaApi || !this.nodes.embla) return;
    if (this.originalSlideCount <= 1) return;
    const container = this.nodes.embla;
    const interactiveSelector = 'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"]), [role="button"], .embla__snip-dots, .embla__snip-bars';
    const mediaQuery = window.matchMedia("(min-width: 992px) and (pointer: fine)");
    let cachedRect = container.getBoundingClientRect();
    const updateRect = () => {
      cachedRect = container.getBoundingClientRect();
    };
    const clickHandler = (e) => {
      if (!mediaQuery.matches) return;
      if (e.target.closest(interactiveSelector)) {
        return;
      }
      const clickX = e.clientX - cachedRect.left;
      if (clickX < cachedRect.width / 2) {
        this.emblaApi.scrollPrev();
      } else {
        this.emblaApi.scrollNext();
      }
    };
    const pointerOverHandler = (e) => {
      if (!mediaQuery.matches) {
        if (container.hasAttribute("data-cursor")) container.removeAttribute("data-cursor");
        return;
      }
      if (e.target.closest(interactiveSelector)) {
        if (container.hasAttribute("data-cursor")) container.removeAttribute("data-cursor");
      } else {
        if (container.getAttribute("data-cursor") !== "slider-nav") container.setAttribute("data-cursor", "slider-nav");
      }
    };
    const pointerLeaveHandler = () => {
      if (container.hasAttribute("data-cursor")) container.removeAttribute("data-cursor");
    };
    const resizeHandler = () => {
      updateRect();
      if (!mediaQuery.matches) {
        container.removeAttribute("data-cursor");
      }
    };
    window.addEventListener("resize", resizeHandler, { passive: true });
    const unbindScroll = onScrollUpdate(updateRect);
    container.addEventListener("click", clickHandler);
    container.addEventListener("pointerover", pointerOverHandler, { passive: true });
    container.addEventListener("pointerleave", pointerLeaveHandler, { passive: true });
    if (mediaQuery.matches) {
      container.setAttribute("data-cursor", "slider-nav");
    }
    const onSelect = () => {
      const index = this.emblaApi.selectedScrollSnap();
      const originalIndex = this.originalSlideCount > 0 ? index % this.originalSlideCount : index;
      const scheme = this.slideColorSchemes[originalIndex];
      if (scheme && scheme !== "inherit") {
        container.dataset.cursorColorScheme = scheme;
      } else {
        delete container.dataset.cursorColorScheme;
      }
      window.dispatchEvent(new CustomEvent("cursor:update"));
    };
    this.emblaApi.on("select", onSelect);
    this.emblaApi.on("reInit", () => {
      updateRect();
      onSelect();
    });
    onSelect();
    this.registerCleanup(() => {
      window.removeEventListener("resize", resizeHandler);
      unbindScroll();
      container.removeEventListener("click", clickHandler);
      container.removeEventListener("pointerover", pointerOverHandler);
      container.removeEventListener("pointerleave", pointerLeaveHandler);
      if (container.hasAttribute("data-cursor")) container.removeAttribute("data-cursor");
      delete container.dataset.cursorColorScheme;
      this.emblaApi.off("select", onSelect);
      this.emblaApi.off("reInit", onSelect);
    });
  }
  // ========== Color Scheme Sync ==========
  enableColorSchemeSync() {
    const cleanup = addColorSchemeSyncHandlers(
      this.emblaApi,
      [this.nodes.paginationContainer, this.nodes.navContainer],
      this.slideColorSchemes,
      this.originalSlideCount
    );
    this.registerCleanup(cleanup);
  }
  // ========== Cleanup ==========
  registerCleanup(cleanup) {
    if (cleanup && typeof cleanup === "function") {
      this.cleanupFunctions.push(cleanup);
    }
  }
  destroy() {
    this.cleanupFunctions.forEach((cleanup) => {
      try {
        cleanup();
      } catch (error) {
      }
    });
    this.cleanupFunctions = [];
    if (this.emblaApi) {
      try {
        this.emblaApi.destroy();
      } catch (error) {
      }
      this.emblaApi = null;
    }
  }
}
if (!customElements.get("article-slideshow")) {
  customElements.define("article-slideshow", ArticleSlideshow);
}
export {
  ArticleSlideshow
};
